import React, { useState } from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import BadgePickerCst from "@components/BadgePickerCst";
import CertificatePickerCst from "@components/CertificatePickerCst";
import { WizardStepProps } from "@components/WizardCst/types";
import { ActivityWizardData } from "../../../types";
import { isFieldVisible } from "../config/additionalInfoConfig";
import SectionHeader from "./SectionHeader";
import MultiAssociationField from "./MultiAssociationField";
import AssociationFieldPlaceholder from "../../MainInfoStep/sections/AssociationFieldPlaceholder";
import {
  PermissionGate,
  PERMISSIONS,
  usePermissions,
} from "@utils/permissions";

const toSelections = (values: { id: number | string; label?: string }[]) =>
  values.map((v) => ({ id: Number(v.id), label: v.label || `#${v.id}` }));

const CertificatesSection: React.FC<WizardStepProps<ActivityWizardData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("wizard-activity");
  const { t: tCert } = useTranslation("certificate-picker");
  const { t: tBadge } = useTranslation("badge-picker");

  const { can } = usePermissions();
  const canViewBadge = can(PERMISSIONS.badge.read);
  const canEditBadge = can(PERMISSIONS.badge.write);

  const showInternalCertificates = isFieldVisible(
    "certificate_ids",
    data.type,
    data.is_standalone,
  );
  const showBadge =
    isFieldVisible("badge_id", data.type, data.is_standalone) && canViewBadge;

  const [internalCertOpen, setInternalCertOpen] = useState(false);
  const [signatureCertOpen, setSignatureCertOpen] = useState(false);
  const [badgeOpen, setBadgeOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SectionHeader
        title={t("sections.certificates.title")}
        description={t("sections.certificates.description") || undefined}
      />

      <Grid container spacing={2}>
        {showInternalCertificates && (
          <Grid item xs={12} md={6}>
            <MultiAssociationField
              label={t("fields.certificate-internal")}
              values={data.certificate_ids}
              onChange={(next) => setData({ certificate_ids: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              onAddClick={() => setInternalCertOpen(true)}
            />
          </Grid>
        )}
        <PermissionGate
          permission={"learning_catalogue.learning_object.signed_certificates"}
          mode="hide"
        >
          <Grid item xs={12} md={6}>
            <MultiAssociationField
              label={t("fields.certificate-signature")}
              values={data.signature_certificate_ids}
              onChange={(next) => setData({ signature_certificate_ids: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              onAddClick={() => setSignatureCertOpen(true)}
            />
          </Grid>
        </PermissionGate>
        {showBadge && (
          <Grid item xs={12} md={6}>
            <AssociationFieldPlaceholder
              label={t("fields.badge")}
              value={data.badge_id}
              onChange={(next) => setData({ badge_id: next })}
              addLabel={t("common.add") || "Aggiungi"}
              emptyLabel={t("common.empty") || "Nessun elemento aggiunto"}
              onAddClick={() => canEditBadge && setBadgeOpen(true)}
              disabled={!canEditBadge}
            />
          </Grid>
        )}
      </Grid>

      {showInternalCertificates && (
        <CertificatePickerCst
          open={internalCertOpen}
          onClose={() => setInternalCertOpen(false)}
          title={tCert("title-internal") || "Associa Certificato interno"}
          initialSelection={toSelections(data.certificate_ids)}
          onConfirm={(selection) => setData({ certificate_ids: selection })}
        />
      )}

      <CertificatePickerCst
        open={signatureCertOpen}
        onClose={() => setSignatureCertOpen(false)}
        title={tCert("title-signature") || "Associa Certificato con firma"}
        initialSelection={toSelections(data.signature_certificate_ids)}
        onConfirm={(selection) =>
          setData({ signature_certificate_ids: selection })
        }
      />

      {showBadge && (
        <BadgePickerCst
          open={badgeOpen}
          onClose={() => setBadgeOpen(false)}
          title={tBadge("title") || "Associa Badge"}
          initialSelection={
            data.badge_id
              ? {
                  id: Number(data.badge_id.id),
                  label: data.badge_id.label || "",
                }
              : null
          }
          onConfirm={(selection) => setData({ badge_id: selection })}
        />
      )}
    </Box>
  );
};

export default CertificatesSection;
